"use client";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@repo/web-ui/components/base/alert";
import { Checkbox } from "@repo/web-ui/components/base/checkbox";
import { Button } from "@repo/web-ui/components/base/button";
import React, { useEffect, useState } from "react";
import { ArrowDownToLine, Terminal, X } from "lucide-react";
import { getLocalStoreItem, setLocalStoreItem, useLocalStore } from "../utils/localStore";
import { SidebarMenuButton, SidebarMenuItem } from "@repo/web-ui/components/base/sidebar";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface Props {
  variant: "prompt" | "sidebarMenuButton";
  state?: "collapsed" | "expanded"
}
const InstallPrompt = ({ variant, state }: Props) => {
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [
    beforeInstallPrompt,
    setBeforeInstallPrompt,
  ] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const { values, setValues } = useLocalStore()

  useEffect(() => {
    setIsIOS(
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    );

    setIsStandalone(window.matchMedia("(display-mode: standalone)").matches);

    const handleBeforeInstallPrompt = (event: Event) => {
      setBeforeInstallPrompt(event as BeforeInstallPromptEvent);
      console.log("beforeInstallPrompt", event);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (!beforeInstallPrompt) return;

    await beforeInstallPrompt.prompt();
    const choiceResult = await beforeInstallPrompt.userChoice;
    if (choiceResult.outcome === "dismissed") {
      setIsDismissed(true);
    }
    setBeforeInstallPrompt(null);
  };

  if (isStandalone) {
    return null; // Don't show install button if already installed
  }

  if (variant === "prompt" && !isDismissed && !values.INSTALL_PROMPT_DECLINED) {
    // IOS implementation
    if (isIOS) {
      return (
        <Alert className="fixed bottom-0 right-0 left-0 w-full z-50">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Install App</AlertTitle>
          <AlertDescription>
            <p>
            To install this app on your iOS device, tap the share button
            <span role="img" aria-label="share icon">
              {" "}
              ⎋{" "}
            </span>
            and then "Add to Home Screen"
            <span role="img" aria-label="plus icon">
              {" "}
              ➕{" "}
            </span>
            .</p>
            <div className="flex justify-end w-full gap-2">
              <div className="flex h-full py-2 items-center gap-2 text-xs">
                <Checkbox
                  onCheckedChange={(checked) => checked !== 'indeterminate' && setValues ? setValues(prev => ({...prev, INSTALL_PROMPT_DECLINED: checked})) : {}}
                />
                <p>Don't prompt me again</p>
              </div>
            </div>
          </AlertDescription>

          <Button
            size={"icon"}
            variant={"ghost"}
            className="absolute top-0 right-0"
            onClick={() => setIsDismissed(true)}
          >
            <X />
          </Button>
        </Alert>
      );
    }

    // non-IOS Prompt
    if (beforeInstallPrompt) {
      return (
        <Alert className="fixed bottom-0 right-0 md:bottom-4 md:right-4 w-full md:max-w-sm z-50">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Install App</AlertTitle>
          <AlertDescription>
            For a better experience, install the app on you phone, tablet, or
            desktop!
              <div className="flex flex-col md:flex-row justify-end w-full gap-2">
                  <div className="flex h-full py-2 items-center gap-2 w-full text-xs">
                  <Checkbox
                      onCheckedChange={(checked) =>
                      setLocalStoreItem(
                          "INSTALL_PROMPT_DECLINED",
                          checked as boolean
                      )
                      }
                  />
                  <p>Don't ask me again</p>
                  </div>
                  <Button variant={"outline"} onClick={() => setIsDismissed(true)}>
                  Dismiss
                  </Button>
                  <Button onClick={handleInstallClick}>Install</Button>
            </div>
          </AlertDescription>
  
          <Button
            size={"icon"}
            variant={"ghost"}
            className="absolute top-0 right-0"
            onClick={() => setIsDismissed(true)}
          >
            <X />
          </Button>
        </Alert>
      );
    }
  }

  if (variant === 'sidebarMenuButton') {
    // IOS implementation
    if (isIOS) {
        return (
            <></>
        )
    }

    if (beforeInstallPrompt) {
        return (
            <SidebarMenuItem>
            <SidebarMenuButton size={'lg'} variant={'outline'} className="text-center justify-center border" onClick={handleInstallClick}>
                {state === 'collapsed' && (
                  <ArrowDownToLine />
                )}
                {state !== 'collapsed' && (
                  <>
                  <ArrowDownToLine />
                  Install App
                  </>
                )}
            </SidebarMenuButton>
            </SidebarMenuItem>
        )
    }
  }

  return null

};

export default InstallPrompt;
